// covered-by: frontends/carrie/src/ask-v2/shell/__tests__/askV2ChatNavigation.test.ts
import { ASK_V2_CHAT_PATH, ASK_V2_PROJECT_PATH, askV2ChatPath } from "../paths";

export type AskV2ChatNavSource = {
  _id: string;
  projectId?: string | null;
  projectChatKind?: "shared" | "personal" | null;
};

export type AskV2RouteMatch = {
  pathname?: string;
  params?: Record<string, unknown>;
};

export function isProjectPersonalChat(chat: AskV2ChatNavSource): boolean {
  return Boolean(chat.projectId && chat.projectChatKind === "personal");
}

/**
 * V2 has no project-scoped chat route yet. Personal project chats use the
 * standalone chat path until that page lands.
 */
export function askV2ChatNavLink(chat: AskV2ChatNavSource) {
  return {
    to: ASK_V2_CHAT_PATH,
    params: { chatId: chat._id },
  } as const;
}

export function askV2ProjectNavLink(projectId: string) {
  return {
    to: ASK_V2_PROJECT_PATH,
    params: { projectId },
  } as const;
}

export type MentionTaskNavSource = {
  chatId: string;
  projectId?: string | null;
  projectChatKind?: "shared" | "personal" | null;
};

/** True when the home task should open the project feed (shared project scope). */
export function mentionTaskOpensProject(task: MentionTaskNavSource): boolean {
  const projectId = task.projectId?.trim();
  if (!projectId) return false;
  // why: unknown kind falls back to the chat route until the chat list resolves
  // personal vs shared — treating unknown as shared misroutes stale personal rows.
  return task.projectChatKind === "shared";
}

/** Home Tasks/ToDos: shared project rows open the project; chat rows open the chat. */
export function mentionTaskNavLink(task: MentionTaskNavSource) {
  const projectId = task.projectId?.trim();
  if (projectId && mentionTaskOpensProject({ ...task, projectId })) {
    return askV2ProjectNavLink(projectId);
  }
  return askV2ChatNavLink({
    _id: task.chatId,
    projectId,
    projectChatKind: task.projectChatKind,
  });
}

export function isAskV2ChatNavActive(
  chat: AskV2ChatNavSource,
  matches: AskV2RouteMatch[],
): boolean {
  return matches.some((m) => m.params?.chatId === chat._id);
}

export function isAskV2ProjectNavActive(
  projectId: string,
  matches: AskV2RouteMatch[],
): boolean {
  return matches.some((m) => m.params?.projectId === projectId);
}

export function isViewingAskV2Chat(
  chat: AskV2ChatNavSource,
  pathname: string,
): boolean {
  return pathname.includes(askV2ChatPath(chat._id));
}
